import React, { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DocsSidebar from '@/features/documentation/components/DocsSidebar';
import { Button, Navigation, NavigationActionLink, SparkIcon } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { useAuth } from '@/features/auth/AuthContext';
import { useUpgradeToProPrompt } from '@/features/billing/hooks/useUpgradeToProPrompt';
import { PlatformUpgrader, UpdateButton, usePlatformUpgraderState } from '@/features/platform-upgrader';
import { FreeUsageExhaustedGate } from '@/features/billing/components/FreeUsageExhaustedGate';
import { DownloadQuoteGate } from '@/features/billing/components/DownloadQuoteGate';
import DeployModelConfirmGate from '@/features/billing/components/DeployModelConfirmGate';
import OutOfCreditsBanner from '@/features/billing/components/OutOfCreditsBanner';
import { BILLING_TAB, settingsTabPath } from '@/features/settings/components/settingsTabs.js';
import { PendingInvitationsProvider } from '@/features/workspaces/components/PendingInvitationsProvider';
import WorkspaceInvitationGate from '@/features/workspaces/components/WorkspaceInvitationGate';
import { useIsPlatformEmpty } from '@/shared/hooks/useIsPlatformEmpty';
import { useIsMobile } from '@/shared/hooks/useMediaQuery';
import { UserMenu } from '@/app/layout/UserMenu';
import '@/app/layout/HorizontalNavBar.css';

const DOC_PATHS = ['installation', 'querying', 'logging', 'external-deploy', 'mcp-server', 'support-doc'];

const TAB_DEFS = [
    { key: 'hello-user', label: 'Projects', path: '/' },
    { key: 'datasets',   label: 'Datasets',  path: '/datasets' },
    { key: 'models',     label: 'Models',    path: '/models' },
    { key: 'analytics', label: 'Analytics',  path: '/analytics' },
    { key: 'installation', label: 'Docs',    path: '/installation' },
];

const FIRST_TIME_TAB_KEYS = new Set(['hello-user', 'installation']);

// specific-builders community invite (Discord).
const COMMUNITY_INVITE_URL = 'https://discord.gg/y3jKERKxy';
const COMMUNITY_LABEL = 'Join specific-builders';

const UPGRADE_LABEL = 'Upgrade to Pro';

function appendUsecaseId(path, usecaseId) {
    return usecaseId ? `${path}?usecaseId=${usecaseId}` : path;
}

const HorizontalNavBar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUsecaseId } = useContext(GlobalContext);
    const { isAuthEnabled, user, role } = useAuth();
    const { isPlatformEmpty } = useIsPlatformEmpty();
    const { showUpgradeToPro } = useUpgradeToProPrompt();
    // ≤ MOBILE_BREAKPOINT: two-row bar (brand + actions, then a scrollable tab
    // strip) and an icon-only community link so nothing overlaps on phones.
    const isMobile = useIsMobile();

    const returnTo = `${location.pathname}${location.search}${location.hash}`;

    const currentPath = location.pathname.slice(1) || 'hello-user';
    const isDocsPage = DOC_PATHS.includes(currentPath);
    const selectedKey = isDocsPage ? 'installation' : currentPath;

    const showTabs = !isAuthEnabled || (user && role !== 'Unassigned');
    const upgrader = usePlatformUpgraderState();

    const visibleTabDefs = isPlatformEmpty
        ? TAB_DEFS.filter((t) => FIRST_TIME_TAB_KEYS.has(t.key))
        : TAB_DEFS;

    const tabs = showTabs
        ? visibleTabDefs.map((t) => ({
            label: t.label,
            state: selectedKey === t.key ? 'active' : 'inactive',
            onClick: () => navigate(appendUsecaseId(t.path, currentUsecaseId)),
        }))
        : [];

    return (
        // The provider wraps the bar so UserMenu (Pending rows, "Welcome aboard"
        // callout) and the gate share one polled invitation list.
        <PendingInvitationsProvider>
            <DocsSidebar currentUsecaseId={currentUsecaseId} />
            <div className="fs-unmask" style={{ position: 'sticky', top: 0, zIndex: 1200, flexShrink: 0 }}>
                <Navigation
                    className="home-nav"
                    tabs={tabs}
                    showBeta
                    stacked={isMobile}
                    actionsStart={<UpdateButton upgrader={upgrader} />}
                    showNotification={upgrader.bellVisible}
                    notificationCount={upgrader.bellCount}
                    onNotificationClick={upgrader.togglePanel}
                    // Figma 9150:4071: Settings lives in the avatar dropdown (UserMenu), not the gear.
                    showSettings={false}
                    leadingActions={(
                        <>
                            <NavigationActionLink href={COMMUNITY_INVITE_URL} iconSlug="discord" iconOnly={isMobile}>
                                {COMMUNITY_LABEL}
                            </NavigationActionLink>
                            {showUpgradeToPro && (
                                <Button
                                    variant="primary"
                                    size="medium"
                                    startIcon={<SparkIcon slug="arrow-up-right" size="medium" />}
                                    onClick={() => navigate(settingsTabPath(BILLING_TAB.id, currentUsecaseId), {
                                        state: { from: returnTo },
                                    })}
                                >
                                    {UPGRADE_LABEL}
                                </Button>
                            )}
                        </>
                    )}
                    rightExtra={<UserMenu />}
                />
            </div>
            {/* Sibling of the sticky bar, not inside it: the strip pushes the
                page's scroll area down instead of covering it. SideBar mounts
                this nav once for every routed screen, so this is the single
                mount point. */}
            <OutOfCreditsBanner />
            <PlatformUpgrader upgrader={upgrader} />
            <FreeUsageExhaustedGate />
            <DownloadQuoteGate />
            <DeployModelConfirmGate />
            <WorkspaceInvitationGate />
        </PendingInvitationsProvider>
    );
};

export default HorizontalNavBar;
